import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import axios from 'axios';
import { MessageCircle, Send, ShieldQuestion, X } from 'lucide-react';
import { buildPageContext } from '../lib/chatContext';
import { fetchComplianceMap } from '../lib/useLiveCompliance';
import { CHAT_WIDGET } from '../constants/testIds';

const API = process.env.REACT_APP_BACKEND_URL;
const SESSION_KEY = 'but_chat_session_id';

const getSessionId = () => {
  let id = localStorage.getItem(SESSION_KEY);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(SESSION_KEY, id);
  }
  return id;
};

const GREETING = {
  role: 'assistant',
  content: "Hi! I'm the BulkUsedTires.com assistant. Ask me about shipping, grading or import compliance for any of our export destinations.",
};

export const ChatWidget = () => {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState(false);
  const sessionId = useRef(getSessionId());
  const listRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/api/chat/history/${sessionId.current}`).then(({ data }) => {
      if (data.length > 0) setMessages([GREETING, ...data]);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, open]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || streaming) return;
    setInput('');
    setError(false);
    setMessages((prev) => [...prev, { role: 'user', content: text }, { role: 'assistant', content: '' }]);
    setStreaming(true);

    try {
      const res = await fetch(`${API}/api/chat/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionId.current,
          message: text,
          page_context: buildPageContext(location.pathname, await fetchComplianceMap()),
        }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split('\n\n');
        buffer = parts.pop();
        for (const part of parts) {
          if (!part.startsWith('data: ')) continue;
          const evt = JSON.parse(part.slice(6));
          if (evt.delta) {
            setMessages((prev) => {
              const next = [...prev];
              next[next.length - 1] = { role: 'assistant', content: next[next.length - 1].content + evt.delta };
              return next;
            });
          } else if (evt.error) {
            setError(true);
          }
        }
      }
    } catch {
      setError(true);
    } finally {
      setStreaming(false);
    }
  };

  return (
    <>
      <motion.button
        data-testid={CHAT_WIDGET.toggleButton}
        onClick={() => setOpen((v) => !v)}
        whileHover={{ scale: 1.08 }}
        whileTap={{ scale: 0.95 }}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-brand-orange hover:bg-brand-orangeHover text-white shadow-2xl flex items-center justify-center"
        aria-label="Open compliance chat assistant"
      >
        {open ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid={CHAT_WIDGET.panel}
            initial={{ opacity: 0, y: 20, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.96 }}
            transition={{ duration: 0.2 }}
            className="fixed bottom-24 right-5 z-50 w-[calc(100vw-2.5rem)] sm:w-[380px] h-[70vh] sm:h-[560px] max-h-[600px] rounded-2xl border border-brand-border bg-brand-surface shadow-2xl flex flex-col overflow-hidden"
          >
            <div className="flex items-center gap-2.5 px-5 py-4 border-b border-brand-border bg-brand-surface2">
              <ShieldQuestion className="w-5 h-5 text-brand-amber shrink-0" />
              <p className="font-display font-bold uppercase text-brand-text text-sm">BulkUsedTires Assistant</p>
              <button
                data-testid={CHAT_WIDGET.closeButton}
                onClick={() => setOpen(false)}
                className="ml-auto text-brand-muted hover:text-brand-orange transition-colors"
                aria-label="Close chat"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div data-testid={CHAT_WIDGET.messageList} ref={listRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
              {messages.map((m, i) => (
                <div
                  key={i}
                  data-testid={CHAT_WIDGET.message(i)}
                  className={`max-w-[85%] rounded-xl px-3.5 py-2.5 text-sm leading-relaxed ${
                    m.role === 'user'
                      ? 'ml-auto bg-brand-orange text-white'
                      : 'bg-brand-bg border border-brand-border text-brand-muted'
                  }`}
                >
                  {m.content || (streaming && i === messages.length - 1 ? '…' : '')}
                </div>
              ))}
              {error && (
                <p data-testid={CHAT_WIDGET.errorBanner} className="text-red-400 text-xs text-center">
                  Something went wrong. Please try again or contact our team directly.
                </p>
              )}
            </div>

            <p data-testid={CHAT_WIDGET.disclaimer} className="px-4 pb-2 text-brand-muted text-[10px] text-center leading-snug">
              AI-generated — always verify critical compliance details with your customs broker.
            </p>

            <div className="flex items-center gap-2 px-4 py-3 border-t border-brand-border">
              <input
                data-testid={CHAT_WIDGET.input}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
                placeholder="Ask about shipping or compliance…"
                disabled={streaming}
                className="flex-1 bg-brand-bg border border-brand-border rounded-full px-4 py-2 text-sm text-brand-text placeholder:text-brand-muted focus:outline-none focus:border-brand-orange"
              />
              <button
                data-testid={CHAT_WIDGET.sendButton}
                onClick={sendMessage}
                disabled={streaming || !input.trim()}
                className="w-9 h-9 rounded-full bg-brand-orange hover:bg-brand-orangeHover disabled:opacity-40 text-white flex items-center justify-center shrink-0 transition-colors"
                aria-label="Send message"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
